<script>
import UsersMixin from "@/views/users/mixins/UsersMixin.vue";
import BaseView from "@/mixins/BaseView.vue";
import SearchField from "@/components/inputs/SearchField.vue";
import TrucksMixin from "@/views/trucks/mixins/TrucksMixin.vue";
import UserSelect from "@/views/users/components/UserSelect.vue";
import TableFilter from "@/components/tables/TableFilter.vue";
import TableFilterRow from "@/components/tables/TableFilterRow.vue";
import ValidationMixin from "@/mixins/validation.mixin.ts";
import CitizenshipSelect from "@/components/selects/CitizenshipSelect.vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import DateRangePicker from "@/components/pickers/DateRangePicker.vue";

const searchParamsKey = "trucks_search";

export default {
  name: "TrucksListSearchForm",
  components: {
    DateRangePicker,
    OmniSelect,
    CitizenshipSelect,
    TableFilterRow,
    TableFilter,
    SearchField,
    UserSelect,
  },
  mixins: [BaseView, UsersMixin, TrucksMixin, ValidationMixin],
  props: {
    errorData: {
      type: [Object, null],
      default: null,
    },
  },
  emits: ["search"],
  data() {
    return {
      params: { availabilityDate: [], locationAddress: "", showNotAvailable: false, type: [] },
      showAdditionalFilters: false,
      validationError: null,
    };
  },
  computed: {
    statuses() {
      return this.truckStatuses.map((status) => {
        status.disabled = false;
        return status;
      });
    },
    isLocationAddressInvalid() {
      return Boolean((this.validationError && !this.params.locationAddress) || this.errorData?.location_address);
    },
  },
  created() {
    this.params = this.getSearchParams(searchParamsKey);
    if (!this.params.radius) this.params.radius = 150;
    if (this.params.locationAddress) {
      this.search();
    }
  },
  methods: {
    clear() {
      this.params = { availabilityDate: [], locationAddress: "", radius: 150, showNotAvailable: false, type: [] };
      this.search();
    },
    async search() {
      if (this.params.phone) {
        this.params.phone = this.params.phone.replaceAll(/\D/g, "").replaceAll(" ", "");
      }

      if (this.params.year && this.params.year.length === 0) {
        delete this.params.year;
      }

      this.setSearchParams(searchParamsKey, this.params);

      if (!this.params.radius || !this.params.locationAddress) {
        this.validationError = true;
        return;
      }

      this.$emit("search", this.params);
    },
  },
};
</script>

<template>
  <table-filter
    :filters="params"
    :header-filter-keys="['locationAddress', 'radius', 'type']"
    @apply="search"
    @clear="clear"
  >
    <template #header>
      <table-filter-row :divider="false" :label="$t('general.filter-main')" icon="mdi-filter-variant">
        <div class="d-flex align-center col-gap-4 pl-3">
          <div class="w-100">
            <search-field
              v-model="params.locationAddress"
              :error="isLocationAddressInvalid"
              :error-message="errorData?.location_address"
              :hide-details="!errorData?.location_address"
              :label="$t('trucks.location')"
              :rules="[requiredValidator]"
              color="primary"
              data-qa="truck-location-field"
              density="compact"
              required
              variant="outlined"
              width="500"
              @search="search"
            />
          </div>

          <v-tooltip location="left">
            <template #activator="{ props: tooltipProps }">
              <v-icon color="primary" v-bind="tooltipProps">mdi-information-outline</v-icon>
            </template>

            You can enter text in the formats: <br />
            New York, NY <br />
            New York - Los Angeles <br />
            10001 - 98108
          </v-tooltip>
        </div>
        <v-col cols="2">
          <search-field
            v-model="params.radius"
            :clearable="false"
            :error="validationError && !params.radius"
            :label="$t('trucks.radius')"
            :rules="[requiredValidator]"
            class="ml-2"
            color="primary"
            data-qa="truck-radius-field"
            density="compact"
            hide-details
            required
            suffix="mi"
            variant="outlined"
            @search="search"
          />
        </v-col>
        <v-col cols="2">
          <omni-select
            v-model="params.type"
            :clearable="true"
            :items="truckTypes"
            :label="$t('trucks.type')"
            :multiple="true"
            data-qa="truck-type-field"
            item-title="name"
            item-value="id"
            @search="search"
          />
        </v-col>
      </table-filter-row>
    </template>
    <table-filter-row :label="$t('trucks.details')" icon="mdi-truck-outline">
      <div class="pl-3 d-flex flex-column justify-center">
        <date-range-picker
          v-model="params.availabilityDate"
          :label="$t('trucks.availability-date')"
          width="500"
          @search="search"
        />
      </div>
      <v-col :style="{ flex: '0 0 100px' }">
        <v-checkbox v-model="params.team" :label="$t('trucks.team')" color="primary" density="compact" hide-details />
      </v-col>
      <v-col :style="{ flex: '0 0 100px' }">
        <v-checkbox v-model="params.signs" :label="$t('trucks.signs')" color="primary" density="compact" hide-details />
      </v-col>
      <v-col cols="3">
        <v-checkbox
          v-model="params.showNotAvailable"
          :label="$t('trucks.show-not-available')"
          color="primary"
          density="compact"
          hide-details
        />
      </v-col>
    </table-filter-row>
    <table-filter-row :label="$t('trucks.trailer-details')" icon="mdi-caravan">
      <v-col class="dimension-part">
        <search-field v-model="params.length" :label="$t('trucks.length')" type="number" @search="search" />
      </v-col>
      <v-col class="dimension-part">
        <search-field v-model="params.width" :label="$t('trucks.width')" type="number" @search="search" />
      </v-col>
      <v-col>
        <search-field v-model="params.height" :label="$t('trucks.height')" type="number" @search="search" />
      </v-col>
      <v-col>
        <search-field v-model="params.year" label="Year from" type="number" @search="search" />
      </v-col>
      <v-col>
        <search-field v-model="params.payload" :label="$t('trucks.payload')" type="number" @search="search" />
      </v-col>
      <v-col>
        <omni-select
          v-model="params.equipment"
          :clearable="true"
          :items="equipment"
          :label="$t('trucks.equipment')"
          :multiple="true"
          item-title="name"
          item-value="id"
          @search="search"
        />
      </v-col>
      <v-col>
        <omni-select
          v-model="params.additionalEquipment"
          :clearable="true"
          :items="additionalEquipment"
          :label="$t('trucks.equipment-extra')"
          :multiple="true"
          item-title="name"
          item-value="id"
          @search="search"
        />
      </v-col>
    </table-filter-row>
    <table-filter-row :divider="false" :label="$t('trucks.owner-driver')" icon="mdi-account-multiple-outline">
      <v-col>
        <user-select v-model="params.owner" :label="$t('users.owner')" owners @change="search" />
      </v-col>
      <v-col>
        <user-select v-model="params.driver" :label="$t('users.driver')" drivers @change="search" />
      </v-col>
      <v-col>
        <omni-select
          v-model="params.driverCertificate"
          :clearable="true"
          :items="driverCertificates"
          :label="$t('drivers.certificates')"
          color="primary"
          density="compact"
          hide-details
          item-title="name"
          item-value="id"
          variant="outlined"
          @update:model-value="search"
        />
      </v-col>
      <v-col>
        <citizenship-select
          v-model="params.driverCitizenship"
          :clearable="true"
          label="Driver citizenship"
          @update:model-value="search"
        />
      </v-col>
      <v-col>
        <omni-select
          v-model="params.willingCrossBorder"
          :clearable="true"
          :items="driverWillingsToCrossBorder"
          :label="$t('drivers.willing-cross-border')"
          color="primary"
          density="compact"
          hide-details
          item-title="name"
          item-value="id"
          variant="outlined"
          @update:model-value="search"
        />
      </v-col>
      <v-col>
        <v-checkbox
          v-model="params.byHomeAddress"
          :label="$t('drivers.search_by_home_location')"
          color="primary"
          density="compact"
          hide-details
          variant="outlined"
          @change="search"
        />
      </v-col>
    </table-filter-row>
  </table-filter>
</template>

<style lang="scss" scoped>
.dimension-part {
  position: relative;

  &:after {
    content: "х";
    position: absolute;
    top: 50%;
    right: -4px;
    transform: translateY(-50%);
  }
}
</style>
